import queryString from "query-string";

/** Aircraft status vocabulary — the SINGLE source for the whole aircraft module.
 *
 *  Codes are the values of `aircraft.status` (app/aircraft/schemas.py:219,
 *  `status: int | None`). The vocabulary itself is strictly a per-tenant config
 *  list, `aircraft_status_list_ac` — g2 holds "0:Active,1:Inactive,2:Archived"
 *  (app/aircraft/fleet_report_filters.py:114-115). Only the fleet report reads
 *  that config today (src/fleet/FleetReportParams.tsx:78); every other aircraft
 *  surface hardcodes the g2 vocabulary, so this constant hardcodes the same
 *  thing rather than inventing a second one. Moving the module onto the config
 *  is a separate change — see the handback note.
 *
 *  This exists because AircraftOverview had drifted onto an INVENTED vocabulary
 *  (1||0=Active, 2="In Maintenance", 3="Grounded"), so an aircraft the General
 *  tab called Archived showed as "In Maintenance" on its own Overview, and an
 *  Inactive one showed as Active. Consume this; do not re-inline a literal.
 */
export const AIRCRAFT_STATUS_LIST: Record<string, string> = {
  "0": "Active",
  "1": "Inactive",
  "2": "Archived",
};

/** Chip palette. Green = in service, amber = withdrawn but live, neutral =
 *  archived. The neutral pair is the module-wide "unrecognised state" colour
 *  (src/flights/columns.tsx:67) — an unknown code must still render, because
 *  the vocabulary is per-tenant and a silently blank chip reads as no data. */
const STATUS_COLOURS: Record<string, { fg: string; bg: string }> = {
  "0": { fg: "#047857", bg: "#ecfdf5" },
  "1": { fg: "#b45309", bg: "#fef3c7" },
  "2": { fg: "#525252", bg: "#f0f0f0" },
};

const UNKNOWN_COLOURS = { fg: "#525252", bg: "#f0f0f0" };

/** Label for a status code. An unmapped code renders as `Status (n)` rather
 *  than silently falling back to "Active" — a wrong label is worse than a
 *  visibly unknown one. */
export const aircraftStatusLabel = (status: number | null | undefined): string => {
  if (status == null) return "Status (—)";
  return AIRCRAFT_STATUS_LIST[String(status)] ?? `Status (${status})`;
};

/** MUI Chip colour for the list's Status column (PATTERNS.md BP-48): the same
 *  green / amber / neutral as the palette above, through the theme. */
export const aircraftStatusChipColor = (
  status: number | null | undefined
): "success" | "warning" | "default" => {
  if (status === 0 || status == null) return "success";
  if (status === 1) return "warning";
  return "default";
};

/* ---- GET /v6/aircraft status filter (A422, Aaron 2026-10-06) --------
 * The list endpoint defaults to Active when a request carries no status
 * filter — the register AND every picker. Active = the tenant's
 * `aircraft_active_status_list` config (default "0"), so the filter's Active
 * option sends the keyword, not a code. A reader that shows HISTORY (a
 * calendar lane, an id→registration map, a record whose aircraft is resolved
 * from the list) must send `withAllAircraftStatuses` or an inactive aircraft
 * goes blank on it. */
export const AIRCRAFT_STATUS_ACTIVE = "active";
export const AIRCRAFT_STATUS_ALL = "all";

/** The Status filter's choices: Active, every labelled non-active code, All.
 *  An unlabelled code (g2 has status 3, which no tenant config names) is only
 *  reachable through All, and shows as `Status (n)`. */
export const AIRCRAFT_STATUS_FILTER_OPTIONS: { value: string; label: string }[] = [
  { value: AIRCRAFT_STATUS_ACTIVE, label: AIRCRAFT_STATUS_LIST["0"] },
  ...Object.entries(AIRCRAFT_STATUS_LIST)
    .filter(([code]) => code !== "0")
    .map(([code, label]) => ({ value: code, label })),
  { value: AIRCRAFT_STATUS_ALL, label: "All" },
];

/** One filter spec, as the list endpoint reads it (`filter=` JSON string). */
export const ALL_AIRCRAFT_STATUSES_SPEC = JSON.stringify({
  field: "status",
  op: "is",
  value: AIRCRAFT_STATUS_ALL,
});

/** Add "every status" to a GET /v6/aircraft params object, keeping any
 *  filter specs it already carries. */
export const withAllAircraftStatuses = <T extends Record<string, any>>(
  params: T
): T & { filter: string[] } => {
  const existing = params.filter;
  const specs: string[] =
    existing == null ? [] : Array.isArray(existing) ? [...existing] : [existing];
  return { ...params, filter: [...specs, ALL_AIRCRAFT_STATUSES_SPEC] };
};

/** The axios config for a history reader of GET /v6/aircraft: every status,
 *  serialised as repeated `filter=` keys. Spread it into the request config.
 *  The serializer is not optional — axios's default writes an array as
 *  `filter[]=`, which the route never reads, so the request would silently
 *  fall back to Active. */
export const allAircraftStatusesRequest = <T extends Record<string, any>>(params: T) => ({
  params: withAllAircraftStatuses(params),
  paramsSerializer: (p: Record<string, any>) => queryString.stringify(p),
});

export const aircraftStatusColours = (
  status: number | null | undefined
): { fg: string; bg: string } =>
  status == null ? UNKNOWN_COLOURS : STATUS_COLOURS[String(status)] ?? UNKNOWN_COLOURS;
